// Headless SEO Engine client.
import titleRiskBriefing from "@/data/title-risk-briefing.json";
//
// All page payloads, sitemap data, and static assets are produced by the
// SEO Engine deployment. This module is the only place in the app that
// talks to the engine — everything else consumes the typed shapes below.

export const SEO_ENGINE_BASE_URL =
  (typeof process !== "undefined" && process.env?.["SEO_ENGINE_BASE_URL"]) ||
  import.meta.env?.VITE_SEO_ENGINE_BASE_URL ||
  "https://seo-engine-aminelarhrib.replit.app";

export const SEO_SITE_KEY = "riadata";
export const SEO_URL_PREFIX = "/r";

// ---------- Types ----------

export type SeoPageType =
  | "property_detail"
  | "guide"
  | "investment_intent"
  | "district"
  | "opportunity"
  | "buyer_use_case"
  | "answer";

export type SeoSectionType =
  | "facts"
  | "pricing"
  | "best_use"
  | "execution"
  | "market_context"
  | "media"
  | "stats"
  | "listings"
  | "guide_intro"
  | "guide_checklist"
  | "featured_listings"
  | "answer_direct"
  | "snapshot"
  | "answer_faq";

export interface SeoSection {
  type: SeoSectionType | string;
  heading?: string | null;
  data?: Record<string, unknown> | null;
  itemsRef?: string | null;
}

export interface SeoFaqItem {
  question: string;
  answer: string;
}

export interface SeoCta {
  label: string;
  href: string;
  type: "lead_capture" | "browse_listings" | "subscribe" | string;
}

export interface SeoInternalLink {
  label: string;
  // Engine returns `url`. We tolerate `href` too for resilience to older payloads.
  url?: string;
  href?: string;
  pageType?: string;
}

export interface SeoMatchedItem {
  id: string;
  externalItemId?: string | null;
  title?: string | null;
  // Engine sometimes returns a structured summary object (district, bestUse, etc).
  // Card renderer only displays it when it's a string; structured fields are
  // surfaced via the dedicated columns on SeoMatchedItem.
  summary?: string | Record<string, unknown> | null;
  coverImageUrl?: string | null;
  url?: string | null;
  // Listing pages (district/opportunity/etc) include richer fields:
  price?: number | null;
  priceCurrency?: string | null;
  positioning?: string | null;
  district?: string | null;
  city?: string | null;
  bestUse?: string | null;
  pricePerM2?: number | null;
  bedrooms?: number | null;
  builtSurfaceM2?: number | null;
  [key: string]: unknown;
}

export interface SeoPage {
  pageType: SeoPageType | string;
  slug: string;
  url: string;
  title: string;
  h1: string;
  introText?: string | null;
  summaryText?: string | null;
  metaDescription: string;
  sections: SeoSection[];
  matchedItems: SeoMatchedItem[];
  faq?: SeoFaqItem[];
  jsonLd?: Record<string, unknown> | Record<string, unknown>[] | null;
  cta?: SeoCta | null;
  internalLinks?: SeoInternalLink[];
  noindex?: boolean;
  lastPublishedAt?: string | null;
  lastDataRefreshAt?: string | null;
  ogImageUrl?: string | null;
  canonicalUrl?: string | null;
  [key: string]: unknown;
}

export interface SeoSitemapEntry {
  slug: string;
  url: string;
  pageType: string;
  supportedLocales?: string[];
  lastPublishedAt?: string | null;
  lastDataRefreshAt?: string | null;
  noindex?: boolean;
  changefreq?: string | null;
  priority?: number | null;
}

export interface SeoSitemap {
  siteKey: string;
  pages: SeoSitemapEntry[];
  generatedAt?: string | null;
}

export function isSeoEntrySupportedForLocale(
  entry: Pick<SeoSitemapEntry, "supportedLocales">,
  locale: string,
): boolean {
  return !entry.supportedLocales || entry.supportedLocales.includes(locale);
}

// ---------- Endpoint URL builders ----------
//
// We keep multiple candidate URL shapes so this client survives small engine
// path changes without forcing a code review. The candidates are tried in
// order until one returns 200.

function pageCandidates(slug: string, pageType?: string): string[] {
  const base = SEO_ENGINE_BASE_URL.replace(/\/$/, "");
  const out: string[] = [];
  if (pageType) {
    out.push(`${base}/api/public/sites/${SEO_SITE_KEY}/pages/${pageType}/${encodeURIComponent(slug)}`);
    out.push(`${base}/api/v1/sites/${SEO_SITE_KEY}/pages/${pageType}/${encodeURIComponent(slug)}`);
    out.push(`${base}/api/sites/${SEO_SITE_KEY}/pages/${pageType}/${encodeURIComponent(slug)}`);
    out.push(`${base}/sites/${SEO_SITE_KEY}/pages/${pageType}/${encodeURIComponent(slug)}`);
  }
  out.push(`${base}/api/public/sites/${SEO_SITE_KEY}/pages/${encodeURIComponent(slug)}`);
  out.push(`${base}/api/v1/sites/${SEO_SITE_KEY}/pages/${encodeURIComponent(slug)}`);
  out.push(`${base}/api/sites/${SEO_SITE_KEY}/pages/${encodeURIComponent(slug)}`);
  out.push(`${base}/sites/${SEO_SITE_KEY}/pages/${encodeURIComponent(slug)}`);
  out.push(`${base}/pages/${encodeURIComponent(slug)}`);
  return out;
}

function sitemapCandidates(): string[] {
  const base = SEO_ENGINE_BASE_URL.replace(/\/$/, "");
  return [
    `${base}/api/public/sites/${SEO_SITE_KEY}/sitemap-data`,
    `${base}/api/v1/sites/${SEO_SITE_KEY}/sitemap-data`,
    `${base}/api/sites/${SEO_SITE_KEY}/sitemap-data`,
    `${base}/sites/${SEO_SITE_KEY}/sitemap-data`,
    `${base}/sitemap-data`,
  ];
}

async function tryFetchJson<T>(urls: string[], init?: RequestInit): Promise<T> {
  let lastErr: unknown = new Error("no candidates");
  for (const url of urls) {
    try {
      const res = await fetch(url, {
        ...init,
        headers: { accept: "application/json", ...(init?.headers ?? {}) },
      });
      if (res.ok) {
        return (await res.json()) as T;
      }
      lastErr = new Error(`${res.status} ${res.statusText} @ ${url}`);
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
}

// ---------- Public API ----------

// Process-local slug → page cache for fetchSeoPage memoization.
const slugCache = new Map<string, SeoPage>();

export function primeSeoPageCache(slug: string, page: SeoPage): void {
  slugCache.set(slug, page);
}

export function getCachedSeoPage(slug: string): SeoPage | undefined {
  return slugCache.get(slug);
}

export function clearSeoPageCache(): void {
  slugCache.clear();
}

export async function fetchSeoPage(
  slug: string,
  pageType?: string,
  init?: RequestInit,
): Promise<SeoPage> {
  const cached = slugCache.get(slug);
  if (cached) return cached;
  if (slug === titleRiskBriefing.slug) {
    const page = titleRiskBriefing as SeoPage;
    slugCache.set(slug, page);
    return page;
  }
  const page = await tryFetchJson<SeoPage>(pageCandidates(slug, pageType), init);
  slugCache.set(slug, page);
  return page;
}

export async function fetchSeoSitemap(init?: RequestInit): Promise<SeoSitemap> {
  const sitemap = await tryFetchJson<SeoSitemap>(sitemapCandidates(), init);
  const hasLocalEntry = sitemap.pages.some(
    (entry) => entry.slug === titleRiskBriefing.slug,
  );
  const pages = sitemap.pages.map((entry) =>
    entry.slug === titleRiskBriefing.slug
      ? { ...entry, supportedLocales: ["en"] }
      : entry,
  );
  return {
    ...sitemap,
    pages: [
      ...pages,
      ...(!hasLocalEntry
        ? [{
            slug: titleRiskBriefing.slug,
            url: titleRiskBriefing.url,
            pageType: titleRiskBriefing.pageType,
            supportedLocales: ["en"],
            lastPublishedAt: titleRiskBriefing.lastPublishedAt,
            lastDataRefreshAt: titleRiskBriefing.lastDataRefreshAt,
            noindex: false,
            changefreq: "monthly",
            priority: 0.7,
          }]
        : []),
    ],
  };
}

// ---------- Per-pageType metadata fallbacks ----------

export const PAGE_TYPE_PRIORITY: Record<string, number> = {
  property_detail: 0.7,
  district: 0.8,
  buyer_use_case: 0.7,
  investment_intent: 0.7,
  opportunity: 0.6,
  guide: 0.5,
  // Answer pages are evergreen high-intent informational surfaces — they
  // sit between district landings and guides on the priority ladder.
  answer: 0.7,
};

// Targets must exist in artifacts/riadata/public/ — the prerender script
// asserts this on every build.
//
// NOTE: property_detail pages generate a per-property dynamic OG image via the
// api-server at /api/v1/og/property/:id (see buildPropertyDetailOgUrl below).
// The fallback pattern below mirrors that endpoint; callers that have a property
// ID should always call buildPropertyDetailOgUrl() instead.
export const PAGE_TYPE_OG_FALLBACK: Record<string, string> = {
  property_detail: "/api/v1/og/property/",
  district: "/opengraph.jpg",
  buyer_use_case: "/opengraph.jpg",
  investment_intent: "/opengraph.jpg",
  opportunity: "/opengraph.jpg",
  guide: "/opengraph.jpg",
  answer: "/opengraph.jpg",
};

/**
 * Build the absolute OG image URL for a property detail page, pointing at the
 * api-server's /api/v1/og/property/:id endpoint.
 *
 * @param apiBase   - Absolute API base URL (e.g. process.env.VITE_LEADS_API_BASE_URL).
 *                    Falls back to `window.location.origin` in dev when unset,
 *                    since the API is proxied through the same origin.
 * @param propertyId - Numeric listing id.
 * @param locale     - Active locale (en | fr | es | de | nl | ru).
 */
export function buildPropertyDetailOgUrl(
  apiBase: string,
  propertyId: number,
  locale: string,
): string {
  const base =
    apiBase.replace(/\/$/, "") ||
    (typeof window !== "undefined" ? window.location.origin : "");
  if (!base) return "";
  return `${base}/api/v1/og/property/${propertyId}?locale=${encodeURIComponent(locale)}`;
}

export function priorityForPageType(pageType?: string | null): number {
  if (!pageType) return 0.6;
  return PAGE_TYPE_PRIORITY[pageType] ?? 0.6;
}

export function ogFallbackForPageType(pageType?: string | null): string {
  if (!pageType) return "/opengraph.jpg";
  return PAGE_TYPE_OG_FALLBACK[pageType] ?? "/opengraph.jpg";
}

// Build an absolute canonical URL given the deployed site's origin.
export function buildCanonicalUrl(siteOrigin: string, slug: string): string {
  const origin = siteOrigin.replace(/\/$/, "");
  return `${origin}${SEO_URL_PREFIX}/${slug}`;
}
